import * as React from 'react'
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import { Circle } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Figma (node 65:341 — Radio Group, node 65:326 — RadioButton):
 * Group: flex flex-col gap-3 (12px)
 * Item container: flex gap-2 (8px) items-start
 * Radio circle: bg-background, border border-border (#e5e5e5), rounded-full, size-4 (16px)
 * Radio circle (selected): inner filled circle size-2 (8px)
 * Radio circle (focus): border-ring (#a3a3a3), shadow-[0px_0px_0px_3px_rgba(163,163,163,0.5)]
 * Radio circle (disabled): opacity-50, cursor-not-allowed
 * Radio circle (invalid): border-destructive (#dc2626), shadow-[0px_0px_0px_3px_rgba(220,38,38,0.2)]
 * Label: text-sm font-medium leading-none text-foreground; invalid: text-destructive
 * Description: text-sm font-normal leading-5 text-muted-foreground
 * Label+desc gap: gap-1.5 (6px), pt-px
 */

export interface IntuiRadioGroupProps
  extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root> {}

const IntuiRadioGroup = React.forwardRef<
  React.ComponentRef<typeof RadioGroupPrimitive.Root>,
  IntuiRadioGroupProps
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Root
    ref={ref}
    className={cn(
      'flex gap-3',
      props.orientation === 'horizontal' ? 'flex-row' : 'flex-col',
      props.disabled && 'cursor-not-allowed',
      className
    )}
    {...props}
  />
))
IntuiRadioGroup.displayName = 'IntuiRadioGroup'

export interface IntuiRadioGroupItemProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>,
    'children'
  > {
  label?: string
  description?: string
  invalid?: boolean
}

const IntuiRadioGroupItem = React.forwardRef<
  React.ComponentRef<typeof RadioGroupPrimitive.Item>,
  IntuiRadioGroupItemProps
>(({ className, label, description, invalid = false, disabled, id, ...props }, ref) => {
  const uid = React.useId()
  const itemId = id ?? `${uid}-${props.value}`

  return (
    <div className="flex gap-2 items-start">
      <RadioGroupPrimitive.Item
        ref={ref}
        id={itemId}
        className={cn(
          'mt-px size-4 shrink-0 rounded-full border bg-background',
          'focus-visible:outline-none',
          invalid
            ? 'border-destructive focus-visible:shadow-[0px_0px_0px_3px_rgba(220,38,38,0.2)]'
            : 'border-border focus-visible:border-ring focus-visible:shadow-[0px_0px_0px_3px_rgba(163,163,163,0.5)]',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
        disabled={disabled}
        {...props}
      >
        <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
          <Circle className="size-2 fill-current stroke-none" />
        </RadioGroupPrimitive.Indicator>
      </RadioGroupPrimitive.Item>
      {label && (
        <label
          htmlFor={itemId}
          className={cn(
            'flex flex-1 flex-col gap-1.5 pt-px',
            disabled ? 'cursor-not-allowed' : 'cursor-pointer'
          )}
        >
          <span
            className={cn(
              'text-sm font-medium leading-none',
              invalid ? 'text-destructive' : 'text-foreground',
              disabled && 'opacity-70'
            )}
          >
            {label}
          </span>
          {description && (
            <span className={cn('text-sm font-normal leading-5 text-muted-foreground', disabled && 'opacity-70')}>
              {description}
            </span>
          )}
        </label>
      )}
    </div>
  )
})
IntuiRadioGroupItem.displayName = 'IntuiRadioGroupItem'

export { IntuiRadioGroup, IntuiRadioGroupItem }
